import { useState, useEffect } from "react";
import axios from "axios";
import { ExternalLink, Disc3 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ReleasesSection = () => {
  const [releases, setReleases] = useState([]);

  useEffect(() => {
    const fetchReleases = async () => {
      try {
        const res = await axios.get(`${API}/releases`);
        setReleases(res.data);
      } catch (err) {
        console.error("Failed to fetch releases:", err);
      }
    };
    fetchReleases();
  }, []);

  const labels = [...new Set(releases.map((r) => r.label).filter(Boolean))];

  return (
    <section id="releases" data-testid="releases-section" className="py-24 md:py-32 tracing-border">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between mb-16">
          <div>
            <p className="font-sub text-[10px] text-white/30 tracking-[0.3em] mb-4">005</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter">RELEASES</h2>
            <div className="gold-line mt-6" />
          </div>
          <a
            data-testid="beatport-profile-link"
            href="https://www.beatport.com/artist/alchemist-ca/1045911"
            target="_blank"
            rel="noopener noreferrer"
            className="font-sub text-xs text-white/40 hover:text-white tracking-widest transition-colors duration-300 underline-offset-4 hover:underline hidden md:inline-flex items-center gap-2"
          >
            View All on Beatport
            <span className="text-white/20">&rarr;</span>
          </a>
        </div>

        {labels.length > 0 && (
          <div className="flex flex-wrap gap-3 mb-12">
            <p className="font-sub text-[10px] text-white/25 tracking-widest mr-2 self-center">LABELS:</p>
            {labels.map((label) => (
              <span key={label} className="font-sub text-[10px] text-white/40 tracking-widest border border-white/10 px-3 py-1.5">
                {label.toUpperCase()}
              </span>
            ))}
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
          {releases.map((release, i) => (
            <a
              key={release.id}
              href={release.beatport_url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`release-${i}`}
              className="group block"
            >
              <div className="relative aspect-square overflow-hidden border border-white/5 mb-3 group-hover:border-white/20 transition-colors duration-300">
                {release.artwork_url ? (
                  <img
                    src={release.artwork_url}
                    alt={release.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                ) : (
                  <div className="absolute inset-0 bg-white/5 flex items-center justify-center">
                    <Disc3 className="w-8 h-8 text-white/10" />
                  </div>
                )}
                <div className="absolute inset-0 bg-[#050505]/30 group-hover:bg-[#050505]/10 transition-colors duration-300" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="bg-[#050505]/80 backdrop-blur-sm border border-white/20 px-4 py-2 flex items-center gap-2">
                    <Disc3 className="w-3 h-3 text-white/70" />
                    <span className="font-sub text-[10px] text-white tracking-widest">BUY</span>
                  </div>
                </div>
                <div className="absolute top-2 left-2 flex gap-1.5">
                  {release.is_new && (
                    <span className="font-sub text-[8px] tracking-widest bg-white text-black px-2 py-0.5">NEW</span>
                  )}
                  {release.is_hype && (
                    <span className="font-sub text-[8px] tracking-widest bg-white/10 border border-white/20 text-white px-2 py-0.5">HYPE</span>
                  )}
                </div>
              </div>
              <h3 className="font-heading text-sm tracking-tight text-white group-hover:text-white/80 truncate">
                {release.title}
              </h3>
              {release.feat && (
                <p className="font-body text-[11px] text-white/30 truncate">feat. {release.feat}</p>
              )}
              <div className="flex items-center gap-2 mt-1">
                <span className="font-sub text-[9px] text-white/25 tracking-widest">{(release.label || "").toUpperCase()}</span>
                <span className="text-white/10">|</span>
                <span className="font-sub text-[9px] text-white/20 tracking-widest">{release.year}</span>
              </div>
            </a>
          ))}
        </div>

        <div className="mt-8 md:hidden">
          <a
            href="https://www.beatport.com/artist/alchemist-ca/1045911"
            target="_blank"
            rel="noopener noreferrer"
            className="font-sub text-xs text-white/40 hover:text-white tracking-widest transition-colors duration-300 underline-offset-4 hover:underline inline-flex items-center gap-2"
          >
            View All on Beatport
            <span className="text-white/20">&rarr;</span>
          </a>
        </div>
      </div>
    </section>
  );
};
